// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useMemo } from 'react'
import { Pager, preferredPageSize, rememberPageSize } from '../../components/Pager'
import { useNavigate } from 'react-router-dom'
import { Search, AlertTriangle, PawPrint } from 'lucide-react'
import * as api from '../../api/client'
import { siteOfCareLabel } from '../../api/siteOfCare'
import { formatPhone, formatDob, } from '../../api/format'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState, EmptyState, relativeTime } from '../../components/workflow/StatePills'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { useColumnManager } from '../../components/ColumnManager'
import { ColumnFilterButton, ColumnFilterChips, funnelToServerOps, type ColumnFilters } from '../../components/common/ColumnFilter'
import { useAuth } from '../../auth/AuthContext'
import { listScrollStyle, stickyHeaderStyle } from '../../styles/listScroll'

// D-VIEWCOLS-1: def-driven table. Column set/order/labels come from the pharmacy's
// admin config (patient_list companion surface); on top of that each USER can
// hide/unhide + reorder via the Columns manager and click headers to sort.
type ColDef = {
  key: string
  label: string
  render: (p: api.PatientListItem) => React.ReactNode
  sortValue?: (p: api.PatientListItem) => string | number
}

export default function PatientListPage() {
  const navigate = useNavigate()
  const [items, setItems] = useState<api.PatientListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  // Search is a SERVER predicate: every distinct value fires a filtered page query AND its COUNT
  // against the tenant DB. 250ms (sized when the filter ran in-browser over already-loaded rows)
  // meant a typed word was up to half a dozen round trips. One second = one query per word.
  const debouncedQ = useDebouncedValue(q, 1000)

  // D-RXFILTER-2: sort and column filters are SERVER predicates now. There is no client fallback —
  // the list is always server-filtered, server-sorted and server-paged, so "next page" is always a
  // network call and the total always counts the whole tenant rather than the rows in memory.
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null)
  const [colFilters, setColFilters] = useState<ColumnFilters>({})

  // Capability comes from the server registry — never a second list here. That second list is how
  // the Rx list drifted (a 9-column sort whitelist against ~40 funnels).
  const [caps, setCaps] = useState<Map<string, api.RxColumnCap>>(new Map())
  const [capsLoaded, setCapsLoaded] = useState(false)
  useEffect(() => {
    api.getPatientColumnCaps()
      .then(r => setCaps(new Map(r.items.map(c => [c.key, c]))))
      .catch(() => { /* degrade to funnels disabled; never block the list */ })
      .finally(() => setCapsLoaded(true))
  }, [])

  // The table's display columns are composite UNITS ("Contact" = phone or email), so they do not map
  // 1:1 onto database columns. This is the one place that translation lives; a registry key that is
  // absent here simply passes through, which is what cfg-driven columns (city, status, ...) need.
  const REGISTRY_KEY: Record<string, string> = {
    patient: 'display_name',
    contact: 'primary_phone',
    insurance: 'insurance_carrier',
    rx: 'rx_active',
    last_activity: 'last_rx_change',
    site_of_care: 'site_of_care_type',
    site_of_care_facility: 'site_of_care_facility',
  }
  const regKey = (uiKey: string) => REGISTRY_KEY[uiKey] ?? uiKey

  // Translate the header-filter state into server filters. A column the registry will not accept is
  // reported, never silently dropped — and never quietly widened into a full-table fetch.
  const { patFilters, blockedCols } = useMemo(() => {
    const pushed: api.RxFilter[] = []
    const stuck: string[] = []
    for (const [uiKey, set] of Object.entries(colFilters)) {
      if (!set || set.size === 0) continue
      const key = regKey(uiKey)
      const cap = caps.get(key)
      if (!cap?.filterable) { stuck.push(uiKey); continue }
      // One translation for every server-paged list (D-UI-2): value lists, from/to bounds and
      // the contains box all go through it, against the operators the registry admits.
      const t = funnelToServerOps(set, cap.ops)
      for (const p of t.preds) pushed.push({ key, ...p })
      if (t.blocked) stuck.push(uiKey)
    }
    return { patFilters: pushed, blockedCols: stuck }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colFilters, caps])

  // Sort pushes down when the registry can order by it; otherwise the server default stands rather
  // than the grid silently sorting only the rows on screen.
  const sortKeySrv = sort && caps.get(regKey(sort.key))?.sortable ? regKey(sort.key) : undefined
  const filterSig = api.encodeRxFilters(patFilters).join('|')
  const [srvPage, setSrvPage] = useState(1)
  const [srvPageSize, setSrvPageSize] = useState(() => preferredPageSize())
  const [srvTruncated, setSrvTruncated] = useState(false)
  // P0: the endpoint now returns an exact total (fail-open null) — same contract as /shipments.
  const [srvTotal, setSrvTotal] = useState<number | null>(null)
  const [srvTotalPages, setSrvTotalPages] = useState<number | null>(null)
  // Reset the server page when the search changes or we re-enter server mode.
  // Search, sort and filters are all part of the QUERY now, so any of them changing changes the
  // result set — page 3 of the old set is meaningless against the new one.
  useEffect(() => { setSrvPage(1) }, [debouncedQ, srvPageSize, filterSig, sortKeySrv, sort?.dir])
  // Respect the pharmacy's patient-header customization: any identity field
  // removed from the patient header is hidden here too (default header → shown).
  // D-CRX-22: specialty badges — batch flags, fail-quiet (module-gated; BioAccess renders as today).
  const [spFlags, setSpFlags] = useState<Record<string, { specialty: boolean; formulary_match: boolean; sample_drug: string | null }>>({})
  // PERF: depend on a STABLE STRING, never on `items` itself. React compares deps
  // with Object.is, so a fresh array reference on every render re-ran this effect
  // — and because it setStates, that render produced another fresh reference. In
  // Patrick's HAR this fired /carerx/patients/specialty-flags 49 TIMES with the
  // IDENTICAL ids parameter, 200 SECONDS of cumulative request time, for one page
  // view. It was the single largest contributor to the UI feeling slow.
  //
  // Same defect as L-077 (me?.permissions re-fetching the pharmacy nine times).
  // The id list only changes when the ids change, which a joined string captures
  // exactly.
  const spIdsKey = useMemo(() => items.map(p => p.id).join(','), [items])
  useEffect(() => {
    if (!spIdsKey) return
    const ids = spIdsKey.split(',')
    const chunks: string[][] = []
    for (let i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200))
    let cancelled = false
    Promise.all(chunks.map(c => api.getCareRxSpecialtyFlags(c).then(r => r.flags).catch(() => ({}))))
      .then(parts => { if (!cancelled) setSpFlags(Object.assign({}, ...parts)) })
    // Guards against an in-flight response from a previous id set landing after a
    // newer one and re-triggering work.
    return () => { cancelled = true }
  }, [spIdsKey])

  const [patientLayout, setPatientLayout] = useState<api.RxViewLayout | null>(null)
  // RCA-CV-5 L7 (E-07): a failed layout read used to fall back to the default columns in
  // silence, so "the portal shows defaults" was indistinguishable from "nothing configured".
  // The page still renders defaults, but says so.
  const [layoutIssue, setLayoutIssue] = useState<string | null>(null)
  useEffect(() => {
    api.getRxViewLayout(undefined, 'patient').then(setPatientLayout)
      .catch(() => setLayoutIssue('The configured patient layout could not be loaded; showing the default columns.'))
  }, [])
  const hide = useMemo(() => (col: string) => api.headerFieldHidden(patientLayout, col), [patientLayout])

  useEffect(() => {
    // ONE request shape. The old hybrid fetched EVERY patient (10k+ on ASP) the moment a filter or
    // sort was active and paged it in the browser, so the page buttons did nothing over the network
    // and the funnel options were whatever happened to be loaded. Filters, sort, search and paging
    // are all server-side now, so this is the only path.
    //
    // Hold until capabilities resolve, but only when something actually depends on them: a cold
    // default view must not wait on a second request to show its first page.
    if (!capsLoaded && (Object.keys(colFilters).length > 0 || sort)) return
    setLoading(true); setError(null)
    api.listPatientsPaged({
      page: srvPage, pageSize: srvPageSize, q: debouncedQ || undefined,
      orderBy: sortKeySrv, orderDir: sortKeySrv ? (sort!.dir === 1 ? 'asc' : 'desc') : undefined,
      filters: patFilters,
    })
      .then(r => {
        setItems(r.items); setSrvTruncated(!!r.truncated)
        setSrvTotal(typeof r.total === 'number' ? r.total : null)
        setSrvTotalPages(typeof r.total_pages === 'number' ? r.total_pages : null)
      })
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srvPage, srvPageSize, debouncedQ, filterSig, sortKeySrv, sort?.dir, capsLoaded])

  // ---- Admin layer: pharmacy home-table column config (patient_list surface). ----
  const [listCfg, setListCfg] = useState<api.RxViewLayout | null>(null)
  useEffect(() => {
    api.getRxViewLayout(undefined, 'patient_list').then(setListCfg)
      .catch(() => setLayoutIssue('The configured column layout could not be loaded; showing the default columns.'))
  }, [])
  const cfgFields = useMemo(() => (((listCfg?.cards as any[] | undefined)?.find(c => c.card_key === 'list_columns')?.fields ?? []) as any[]), [listCfg])
  // D-VIEWCOLS-2b: per-column sub-text (second line) suppression.
  const subHidden = useMemo(() => new Set(((((listCfg?.cards as any[] | undefined)?.find(c => c.card_key === 'list_columns')?.config as any)?.subtext_hidden ?? []) as string[])), [listCfg])

  // Custom field values, batch-resolved for the visible page (same flow as the Prescriptions queue).
  const [cfValues, setCfValues] = useState<Record<string, Record<string, string | null>>>({})
  const cfVal = (id: string, col: string) => {
    const row = cfValues[id]; if (!row) return '—'
    const k = Object.keys(row).find(x => x.endsWith(`.${col}`))
    return (k && row[k]) || '—'
  }

  // Standard column units. RBAC field rules still apply inside and to whole units.
  const baseDefs: ColDef[] = useMemo(() => {
    const units: ColDef[] = [
      {
        key: 'patient', label: 'Patient',
        sortValue: p => (p.display_name ?? '').toLowerCase(),
        render: p => (
          <>
            <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
              {p.display_name}
              {p.is_animal && <PawPrint size={12} style={{ color: '#a16207' }} aria-label="Veterinary patient" />}
              {spFlags[p.id] && (
                <span title={spFlags[p.id].sample_drug ?? undefined} style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: .3, textTransform: 'uppercase', padding: '1px 7px', borderRadius: 999,
                  background: spFlags[p.id].specialty ? '#f3e8ff' : '#fffbeb',
                  color: spFlags[p.id].specialty ? '#7c3aed' : '#92400e',
                  border: `1px solid ${spFlags[p.id].specialty ? '#a855f7' : '#d97706'}`,
                }}>Specialty</span>
              )}
            </div>
          </>
        ),
      },
      ...(!hide('date_of_birth') ? [{
        key: 'date_of_birth', label: 'DOB',
        sortValue: (p: api.PatientListItem) => p.date_of_birth ?? '',
        render: (p: api.PatientListItem) => (
          <>
            <div>{formatDob(p.date_of_birth)}</div>
            {!hide('gender') && !subHidden.has('date_of_birth') && <div style={mutedSm}>{p.gender ?? p.sex ?? '—'}</div>}
          </>
        ),
      }] : []),
      {
        key: 'contact', label: 'Contact',
        sortValue: p => (p.primary_phone ?? p.email ?? '').toLowerCase(),
        render: p => (
          <>
            {!hide('primary_phone') && <div>{formatPhone(p.primary_phone)}</div>}
            {!subHidden.has('contact') && <div className="email-text" style={mutedSm}>{p.email ?? ''}</div>}
          </>
        ),
      },
      {
        key: 'insurance', label: 'Insurance',
        sortValue: p => (p.insurance_carrier ?? '').toLowerCase(),
        render: p => <>{p.insurance_carrier ?? <span style={mutedSm}>Cash pay</span>}</>,
      },
      ...(!hide('allergies') ? [{
        key: 'allergies', label: 'Allergies',
        sortValue: (p: api.PatientListItem) => (p.allergies ?? '').toLowerCase(),
        render: (p: api.PatientListItem) => (
          p.allergies && p.allergies !== 'None' ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#991b1b' }}>
              <AlertTriangle size={12} aria-hidden="true" /> {p.allergies}
            </span>
          ) : <span style={mutedSm}>None</span>
        ),
      }] : []),
      {
        key: 'rx', label: 'Rx',
        sortValue: p => p.rx_active ?? 0,
        render: p => (
          <>
            <strong>{p.rx_active}</strong>
            {!subHidden.has('rx') && <span style={mutedSm}> active / {p.rx_total} total</span>}
          </>
        ),
      },
      {
        key: 'last_activity', label: 'Last activity',
        sortValue: p => p.last_rx_change ?? '',
        render: p => <>{p.last_rx_change ? relativeTime(p.last_rx_change) : '—'}</>,
      },
      // D-SOC-2: the patient's current site of care (residential / facility type) and the facility.
      {
        key: 'site_of_care', label: 'Site of care',
        sortValue: p => p.site_of_care_type ?? '',
        render: p => {
          const t = p.site_of_care_type
          if (!t) return <span style={mutedSm}>—</span>
          const res = t === 'residential'
          return <span style={{ fontSize: 11.5, padding: '2px 9px', borderRadius: 20, background: res ? '#F1EFE8' : '#fef3c7', color: res ? '#444441' : '#92400e', fontWeight: 600, whiteSpace: 'nowrap' }}>{siteOfCareLabel(t)}</span>
        },
      },
      {
        key: 'site_of_care_facility', label: 'Facility',
        sortValue: p => p.site_of_care_facility ?? '',
        render: p => <>{p.site_of_care_facility ?? <span style={mutedSm}>—</span>}</>,
      },
    ]
    if (!cfgFields.length) return units
    const byKey = new Map(units.map(d => [d.key, d]))
    const out: ColDef[] = []
    for (const f of cfgFields) {
      if (!f.visible) continue
      if (f.owlrx_table === 'custom') {
        out.push({
          key: `cf:${f.owlrx_column}`,
          label: (f.owlrx_label || '').trim() || f.owlrx_column,
          render: p => <>{cfVal(p.id, f.owlrx_column)}</>,
        })
      } else {
        const d = byKey.get(f.owlrx_column)
        if (d) out.push({ ...d, label: (f.owlrx_label || '').trim() || d.label })
      }
    }
    // Units the admin config predates (added later in code) stay visible by default.
    const seen = new Set(out.map(d => d.key))
    const mentioned = new Set(cfgFields.map((f: any) => f.owlrx_table === 'custom' ? `cf:${f.owlrx_column}` : f.owlrx_column))
    out.push(...units.filter(d => !seen.has(d.key) && !mentioned.has(d.key)))
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfgFields, hide, cfValues, subHidden])

  // ---- User layer: per-user hide/unhide + reorder, persisted per browser. ----
  const allLabels = useMemo(() => baseDefs.map(d => d.label), [baseDefs])
  const { visibleColumns: visLabels, managerUI, toggleButton } = useColumnManager('patients_list', allLabels)
  // D-VIEWCOLS-2a: columns the pharmacy admin hid from the provider view.
  const { me } = useAuth()
  const isProvider = me?.role_name === 'provider' || me?.role_name === 'provider_nurse'
  const provHidden = useMemo(() => new Set(((((listCfg?.cards as any[] | undefined)?.find(c => c.card_key === 'list_columns')?.config as any)?.provider_hidden ?? []) as string[])), [listCfg])
  const cols = useMemo(() => {
    const byLabel = new Map(baseDefs.map(d => [d.label, d]))
    return visLabels.map(l => byLabel.get(l))
      .filter((d): d is ColDef => !!d)
      .filter(d => !isProvider || !provHidden.has(d.key))
  }, [baseDefs, visLabels, isProvider, provHidden])

  // ---- Sort (header click), applied before pagination. ----
  const toggleSort = (key: string) => setSort(s => (s?.key === key ? (s.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 }))

  // The client filter pipeline is GONE. Rows arrive filtered, sorted and paged by the database, so
  // re-deriving anything here would filter a page and report it as a set — and the funnel options
  // now come from /patients/facets rather than from whatever is on screen.

  const pageRows = items
  const page = srvPage
  const setPage = setSrvPage
  const pageSize = srvPageSize
  const setPageSize = (n: number) => { rememberPageSize(n); setSrvPageSize(n) }
  // The endpoint's exact total ("1–10 of 8,432"). When the fail-open count timed out we advertise
  // one extra page while truncated so Next stays usable, rather than inventing a number.
  const totalPages = srvTotalPages ?? (srvTruncated ? srvPage + 1 : srvPage)
  const total = srvTotal ?? ((srvPage - 1) * srvPageSize + items.length + (srvTruncated ? 1 : 0))

  const customCols = useMemo(() => cfgFields.filter((x: any) => x.owlrx_table === 'custom' && x.visible), [cfgFields])
  useEffect(() => {
    if (customCols.length === 0 || pageRows.length === 0) return
    let dead = false
    api.batchCustomFieldValues('patient', pageRows.map(p => p.id))
      .then(r => { if (!dead) setCfValues(v => ({ ...v, ...(r.values ?? {}) })) }).catch(() => {})
    return () => { dead = true }
  }, [customCols.length, pageRows])

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <h1 style={titleStyle}>Patients</h1>
        {/* P0: in server mode items.length is ONE PAGE (it read "10 patients" on an 8k tenant) —
            show the real matching total the pager uses. */}
        <div style={subtitleStyle}>{total.toLocaleString()} {total === 1 ? 'patient' : 'patients'}</div>
      </header>

      <div style={{ ...searchRow, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ ...searchWrap, flex: 1 }}>
          <Search size={14} aria-hidden="true" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-mute)' }} />
          <input
            type="text"
            placeholder="Search by name, email, or DOB…"
            value={q}
            onChange={e => setQ(e.target.value)}
            style={searchInput}
          />
        </div>
        {toggleButton}
      </div>
      {managerUI}

      {layoutIssue && <ErrorBanner message={layoutIssue} onClose={() => setLayoutIssue(null)} />}
      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      <div style={cardStyle}>
        {loading ? (
          <LoadingState label="Loading patients…" />
        ) : pageRows.length === 0 ? (
          <EmptyState message={q ? `No patients match "${q}".` : 'No patients found.'} />
        ) : (
          <>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="patients" />
          <ColumnFilterChips filters={colFilters} labels={Object.fromEntries(baseDefs.map(d => [d.key, d.label]))} onClear={k => setColFilters(f => ({ ...f, [k]: new Set() }))} onClearAll={() => setColFilters({})} />
          {blockedCols.length > 0 && (
            <div style={{ fontSize: 12.5, padding: '7px 10px', marginBottom: 8, borderRadius: 8, border: '1px solid #f59e0b', background: '#fffbeb', color: '#78350f' }}>
              {/* A filter the server has no expression for is refused and NAMED, never silently
                  ignored and never widened into a full-table fetch. */}
              <b>{blockedCols.map(k => baseDefs.find(d => d.key === k)?.label ?? k).join(', ')}</b>
              {blockedCols.length > 1 ? ' cannot be filtered' : ' cannot be filtered'} on the server, so
              {blockedCols.length > 1 ? ' they are' : ' it is'} not applied. Everything else below is filtered across all {total.toLocaleString()} patients.
            </div>
          )}
          <div style={listScrollStyle(300)}>
      <table style={tableStyle}>
            <thead>
              <tr>
                {cols.map(c => (
                  <th
                    key={c.key}
                    style={{ ...thStyle, cursor: c.sortValue ? 'pointer' : 'default', userSelect: 'none' }}
                    onClick={c.sortValue ? () => toggleSort(c.key) : undefined}
                    aria-sort={sort?.key === c.key ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}
                  >
                    {c.label}{sort?.key === c.key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}{' '}
                    {(() => {
                      // Server-backed options, or none. A loaded-rows funnel here would offer the
                      // carriers on THIS page and hide the rest — a sample dressed as a filter,
                      // which is the behaviour this change removes. Identity columns (name, MRN,
                      // phone, email) are refused by the registry on purpose: searchable, never
                      // enumerable.
                      const rk = regKey(c.key)
                      const cap = caps.get(rk)
                      const isDate = cap?.klass === 'date'
                      // D-RXFILTER-4 added the numeric range control to the shared funnel; rx_total
                      // and rx_active already accepted gte/lte at the API with no way to reach it.
                      const isNumeric = cap?.klass === 'numeric'
                      // D-UI-2: a column the registry filters by CONTAINS and never lists (a name,
                      // an MRN, a phone number) gets the funnel with a text box in it.
                      const isText = !!cap?.filterable && !cap.facetable && !isDate && !isNumeric && cap.ops.includes('contains')
                      if (!cap?.facetable && !isDate && !isNumeric && !isText) return (
                        <span title={cap?.reason ?? `${c.label} cannot be filtered on the server.`}
                          aria-label={`${c.label} filter unavailable`}
                          style={{ display: 'inline-block', width: 14, opacity: 0.25, cursor: 'not-allowed', fontSize: 11 }}>⌀</span>
                      )
                      return (
                        <ColumnFilterButton label={c.label}
                          isDate={isDate}
                          isNumeric={isNumeric}
                          isText={isText}
                          fetchOptions={cap?.facetable ? (term: string) => api.getPatientFacet({
                            column: rk,
                            q: term,
                            // Spreadsheet semantics: this column keeps its full option list while it
                            // is filtered; every OTHER active filter narrows it.
                            filters: patFilters.filter(f => f.key !== rk),
                          }) : undefined}
                          selected={colFilters[c.key] ?? new Set()}
                          onChange={next => { setColFilters(f => ({ ...f, [c.key]: next })); setPage(1) }} />
                      )
                    })()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map(p => (
                <tr
                  key={p.id}
                  style={trStyle}
                  onClick={() => navigate(`/patients/${p.id}`)}
                  onKeyDown={e => { if (e.key === 'Enter') navigate(`/patients/${p.id}`) }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open patient ${p.display_name}`}
                >
                  {cols.map(c => <td key={c.key} style={tdStyle}>{c.render(p)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
      </div>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="patients" />
          </>
        )}
      </div>
    </div>
  )
}

const pageStyle: React.CSSProperties = { maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', padding: '24px 24px 48px' }
const headerStyle: React.CSSProperties = { marginBottom: 16 }
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: -0.5,
}
const subtitleStyle: React.CSSProperties = { marginTop: 4, color: 'var(--color-text-soft)', fontSize: 13 }
const searchRow: React.CSSProperties = { marginBottom: 16 }
const searchWrap: React.CSSProperties = { position: 'relative', maxWidth: 400 }
const searchInput: React.CSSProperties = {
  width: '100%', padding: '8px 12px 8px 32px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 6,
  fontFamily: 'inherit', boxSizing: 'border-box',
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8, overflow: 'hidden',
}
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 }
const thStyle: React.CSSProperties = {
  ...stickyHeaderStyle,
  textAlign: 'left', padding: '10px 14px',
  background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: 'var(--color-text-mute)',
}
const trStyle: React.CSSProperties = { borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }
const tdStyle: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'middle' }
const mutedSm: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-mute)' }
